/**
 * Tipi del contratto `/api/v2/`.
 * ---------------------------------------------------------------------------
 * NON sono più assunzioni: sono derivati dallo schema OpenAPI reale del backend
 * (`../docs/openapi-v2.yaml`, 3.683 righe), parsato campo per campo.
 * La riconciliazione con le 10 assunzioni iniziali è in `../docs/contratto-api-v2.md`.
 *
 * Convenzioni del backend, da conoscere prima di leggere il resto:
 *
 *  1. **Lo schema è in italiano.** `risultati`, `punti_interesse`, `copertura`,
 *     `identita_narrativa`. I tipi seguono il backend: reintrodurre nomi inglesi
 *     darebbe un'API bilingue.
 *
 *  2. **`provincia` è la SIGLA, `provincia_nome` è il nome esteso.**
 *     Attenzione: è l'inverso di come il frontend li aveva chiamati prima
 *     (`provincia` = nome, `provincia_sigla` = sigla).
 *
 *  3. **I decimali arrivano come STRINGHE.** È il default di `DecimalField` in
 *     DRF: `latitudine`, `longitudine`, `centro_lat`, `centro_lon`,
 *     `superficie_kmq`. Vanno convertiti con `toNumber()` prima di passarli a
 *     Leaflet, che con una stringa non fallisce: piazza il marker a `NaN,NaN`
 *     e lo fa sparire in silenzio.
 *
 *  4. **L'assenza.** `null` = valore non noto; `[]` = elenco vuoto; stringa
 *     vuota = campo non compilato. Tutti e tre resi come "non disponibile".
 *     La copertura per sezione la dichiara il backend in `copertura`.
 */

// ---------------------------------------------------------------------------
// Primitive
// ---------------------------------------------------------------------------

/** `SiglaProvincia` — enum chiuso nello schema. */
export type SiglaProvincia = 'BA' | 'BAT' | 'BR' | 'FG' | 'LE' | 'TA';

/** `PianoComune` — livello di adesione. */
export type PianoComune = 'base' | 'pro';

/** `LayerMappa` — sfondo cartografico preferito dal comune. */
export type LayerMappa = 'osm' | 'cartocdn' | 'satellite';

/**
 * Decimale serializzato da DRF: **stringa**, non numero.
 * L'alias esiste per rendere il fatto visibile nel tipo, invece di lasciarlo
 * come un `string` qualunque che prima o poi qualcuno passa a Leaflet.
 */
export type DecimalString = string;

/** Converte un decimale DRF in `number`. `null` se non è un numero valido. */
export function toNumber(
  value: DecimalString | number | null | undefined,
): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * `Coordinate` — componente dichiarato: `{lat, lon}`, entrambi obbligatori e
 * `string` con `format: decimal`.
 *
 * Prima della rigenerazione dello schema qui c'era un'incoerenza: `coordinate`
 * era un `object` non tipizzato e usciva con `lat` **numero**, mentre
 * `mappa.centro_lat` usciva **stringa** nella stessa risposta. Ora sono
 * entrambi decimali-stringa.
 *
 * I campi restano opzionali in questo tipo e `coordinateOf()` accetta ancora
 * numeri e nomi alternativi: è una lettura tollerante deliberata, perché le
 * fixture del portale statico hanno `lat` numerico e la stessa funzione serve
 * entrambe le sorgenti.
 */
export interface Coordinate {
  readonly lat?: DecimalString | number;
  readonly lon?: DecimalString | number;
  readonly latitude?: DecimalString | number;
  readonly longitude?: DecimalString | number;
}

/** Estrae una coppia lat/lon usabile, o `null`. Tollera stringhe e nomi alternativi. */
export function coordinateOf(
  value: Coordinate | null | undefined,
): { lat: number; lon: number } | null {
  if (!value) return null;
  const lat = toNumber(value.lat ?? value.latitude ?? null);
  const lon = toNumber(value.lon ?? value.longitude ?? null);
  if (lat === null || lon === null) return null;
  return { lat, lon };
}

// ---------------------------------------------------------------------------
// Paginazione — `{meta, risultati}`, NON `{count, next, previous, results}`
// ---------------------------------------------------------------------------

/**
 * `Licenza` — componente dichiarato. Tutti i campi obbligatori.
 * `share_alike` e `richiede_attribuzione` sono booleani: l'obbligo di licenza
 * è un dato, non una stringa da interpretare.
 */
export interface Licenza {
  readonly codice: string;
  readonly nome: string;
  readonly url: string;
  readonly share_alike: boolean;
  readonly richiede_attribuzione: boolean;
}

/**
 * `Attribuzione` — componente dichiarato, tutti i campi obbligatori.
 * Compare in `meta.attribuzione` di ogni risposta paginata.
 *
 * `campi` elenca quali campi del risultato provengono da questa fonte: è ciò
 * che rende possibile l'attribuzione **per campo** richiesta dal vincolo 5,
 * senza ripetere licenza e URL su ogni riga.
 */
export interface Attribuzione {
  /** Codice della fonte: è la chiave che compare come `fonte` sulle voci. */
  readonly fonte: string;
  readonly nome: string;
  readonly ente: string;
  readonly url: string;
  readonly licenza: Licenza;
  readonly share_alike: boolean;
  /** Testo di attribuzione già pronto da mostrare. */
  readonly attribuzione: string;
  readonly data_snapshot: string | null;
  readonly aggiornato_il: string | null;
  /** Campi del risultato coperti da questa fonte. */
  readonly campi: readonly string[];
}

export interface PaginationMeta {
  readonly totale: number;
  readonly pagina: number;
  readonly pagine: number;
  readonly per_pagina: number;
  readonly prossima: string | null;
  readonly precedente: string | null;
  /** Attribuzione delle fonti dei risultati di questa pagina. */
  readonly attribuzione?: readonly Attribuzione[];
}

/** Busta di paginazione del backend. Query: `?pagina=N&per_pagina=M`. */
export interface Paginated<T> {
  readonly meta: PaginationMeta;
  readonly risultati: readonly T[];
}

/**
 * Busta degli endpoint NON paginati (`/eventi/calendario/`, decisione 205):
 * `meta` porta SOLO `totale` e `attribuzione` — verificato sulla risposta
 * viva. Dichiarare qui `Paginated` significherebbe promettere `pagina`,
 * `per_pagina` e cursori che non esistono.
 */
export interface NonPaginated<T> {
  readonly meta: Pick<PaginationMeta, 'totale' | 'attribuzione'>;
  readonly risultati: readonly T[];
}

// ---------------------------------------------------------------------------
// Entità di supporto del comune
// ---------------------------------------------------------------------------

/**
 * Campi comuni a tutte le entità elencate nella scheda comune. Nello schema si
 * ripetono identici su ServizioComunale, Scuola, Biblioteca,
 * StrutturaSanitaria, ServizioSociale, Trasporto, StrutturaRicettiva e
 * PuntoInteresse.
 */
export interface VoceComune {
  readonly id: number;
  readonly nome: string;
  readonly indirizzo?: string;
  readonly telefono?: string;
  readonly email?: string;
  readonly sito?: string;
  readonly orari?: string;
  /** Codice della fonte. Il join con licenza e URL passa da `/api/v2/fonti/`. */
  readonly fonte: string;
}

export interface ServizioComunale extends VoceComune {
  readonly tipo?: string;
  readonly icona?: string;
}

export interface Scuola extends VoceComune {
  readonly grado?: string;
  readonly gestione?: string;
  readonly tipo_dichiarato?: string;
  readonly plesso?: string;
  readonly codice_meccanografico?: string;
  readonly settore?: string;
}

export type Biblioteca = VoceComune;

export interface StrutturaSanitaria extends VoceComune {
  readonly tipo?: string;
}

export interface ServizioSociale extends VoceComune {
  readonly categoria?: string;
}

export interface Trasporto extends VoceComune {
  readonly tipo?: string;
}

export interface StrutturaRicettiva extends VoceComune {
  readonly categoria?: string;
  readonly latitudine?: DecimalString | null;
  readonly longitudine?: DecimalString | null;
  /** **Booleani**, non stringhe: `true` / `false` / `null` (non dichiarato). */
  readonly accessibilita_interna?: boolean | null;
  readonly accessibilita_esterna?: boolean | null;
  readonly servizi_dichiarati?: unknown;
  readonly servizi_disabili?: unknown;
  readonly periodo_apertura?: string;
  readonly foto_url?: string | null;
}

/** `CategoriaStrutturaRicettiva` — enum chiuso dello schema. */
export type CategoriaStrutturaRicettiva =
  | 'albergo'
  | 'b_and_b'
  | 'agriturismo'
  | 'ostello'
  | 'campeggio'
  | 'casa_vacanza'
  | 'lido'
  | 'altro';

/**
 * `StrutturaRicettivaElenco` — voce dell'elenco TRASVERSALE
 * `GET /strutture-ricettive/` (coda rimisura 31/07/2026, per la pagina-dato
 * /territorio/mare-e-lidi/): la `StrutturaRicettiva` della scheda comune più
 * il comune di appartenenza, che lì era implicito.
 */
export type StrutturaRicettivaElenco = StrutturaRicettiva & {
  /** Slug del comune, con underscore (vincolo 3). */
  readonly comune: string;
  readonly comune_nome: string;
  readonly provincia: SiglaProvincia;
};

/** `TipoPuntoInteresse` — enum chiuso di 15 valori. Serve alle chiavi i18n. */
export type TipoPuntoInteresse =
  | 'chiesa'
  | 'castello'
  | 'museo'
  | 'palazzo_storico'
  | 'teatro_storico'
  | 'grotta'
  | 'parco_archeologico'
  | 'archeologia'
  | 'monumento'
  | 'natura'
  | 'parco_giardino'
  | 'archivio'
  | 'biblioteca'
  | 'infrastruttura'
  | 'altro';

export const TIPI_PUNTO_INTERESSE: readonly TipoPuntoInteresse[] = [
  'chiesa',
  'castello',
  'museo',
  'palazzo_storico',
  'teatro_storico',
  'grotta',
  'parco_archeologico',
  'archeologia',
  'monumento',
  'natura',
  'parco_giardino',
  'archivio',
  'biblioteca',
  'infrastruttura',
  'altro',
];

/**
 * Punto di interesse. **Ha le coordinate.**
 *
 * La limitazione dichiarata in precedenza dal frontend ("i POI hanno solo
 * nome/tipo/indirizzo, quindi la mappa mostra solo il centro del comune") non
 * è più valida: `latitudine` e `longitudine` esistono. I campi `tutela`,
 * `soprintendenza` e `link_approfondimento` arrivano dai 4.166 beni culturali
 * MiC.
 */
export interface PuntoInteresse extends VoceComune {
  readonly tipo?: TipoPuntoInteresse;
  readonly tipo_dichiarato?: string;
  readonly descrizione_breve?: string;
  readonly latitudine?: DecimalString | null;
  readonly longitudine?: DecimalString | null;
  readonly immagine_url?: string | null;
  readonly link_approfondimento?: string | null;
  readonly tutela?: string;
  readonly soprintendenza?: string;
  readonly visitabile?: boolean | null;
}

export interface Contatti {
  readonly centralino?: string;
  readonly urp_telefono?: string;
  readonly email?: string | null;
  readonly pec?: string;
  readonly sito_ufficiale?: string | null;
  readonly indirizzo?: string;
}

export interface IdentitaNarrativa {
  readonly testo: string;
  /**
   * Gemello in SOLA LETTURA di `testo`, senza marcatura (decisione 96 del
   * backend). Da usare ovunque l'HTML sia un difetto e non un contenuto:
   * sintesi vocale, `<meta>`, anteprime, ricerca. Non esiste in scrittura.
   */
  readonly testo_semplice?: string;
  readonly stato?: string;
  readonly lingua?: string;
}

/** Preferenze cartografiche del comune. Decimali come stringhe. */
export interface Mappa {
  readonly centro_lat: DecimalString;
  readonly centro_lon: DecimalString;
  /** Voce 45a (sigillo 01/08): il CENTRO ABITATO dal congelato del
   *  riferimento (per Brindisi 40.6415/17.9465, il porto) — distinto da
   *  `centro_*`, che è il baricentro geometrico del territorio comunale
   *  (per Brindisi cade in periferia sulla SS7). `null` = il congelato non
   *  lo dichiara: si ripiega sul baricentro, dichiarandolo nel codice. */
  readonly centro_abitato_lat: DecimalString | null;
  readonly centro_abitato_lon: DecimalString | null;
  readonly zoom_default?: number;
  readonly layer_default?: LayerMappa;
}

/** `highlights` è dichiarato solo come "Elenco di stringhe brevi". */
export interface Overview {
  readonly highlights?: readonly string[];
  readonly completezza_percent?: number | null;
}

/**
 * Estratto Wikipedia — **campo a sé** su `ComuneDettaglio`, non dentro
 * `overview`. È la sola fonte **CC BY-SA** del portale: tenerla separata rende
 * più difficile sbagliare l'attribuzione (vincolo 5).
 */
export interface EstrattoWikipedia {
  readonly titolo: string;
  readonly estratto: string;
  readonly descrizione?: string;
  readonly url: string;
  readonly thumbnail_url?: string | null;
  readonly lingua?: string;
  readonly licenza: string;
}

/**
 * `copertura` — `object` con `additionalProperties` libere. Descrizione nello
 * schema: *"Quali sezioni hanno contenuto. Rende l'assenza uno stato di prima
 * classe dell'API, non qualcosa da dedurre contando gli array."*
 *
 * È il vincolo 4 della specifica arrivato fino al contratto. La forma dei
 * valori non è dichiarata, quindi il tipo resta permissivo e l'accesso passa
 * da `sezionePopolata()`.
 */
export type Copertura = Readonly<Record<string, unknown>>;

/** Legge `copertura` senza dare per scontata la forma dei valori. */
export function sezionePopolata(
  copertura: Copertura | undefined,
  sezione: string,
): boolean | null {
  if (!copertura || !(sezione in copertura)) return null;
  const value = copertura[sezione];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  if (Array.isArray(value)) return value.length > 0;
  return null;
}

// ---------------------------------------------------------------------------
// Comune
// ---------------------------------------------------------------------------

/** `ComuneSintesi` — la voce dell'indice. Non porta i sottoelenchi. */
export interface ComuneSintesi {
  readonly slug: string;
  readonly nome: string;
  /** SIGLA (`BR`), non il nome esteso. */
  readonly provincia: SiglaProvincia;
  /** Nome esteso (`Brindisi`). */
  readonly provincia_nome: string;
  readonly codice_istat?: string;
  readonly cap?: string;
  readonly popolazione?: number | null;
  readonly coordinate: Coordinate | null;
  readonly attivo?: boolean;
  readonly piano?: PianoComune;

  /* Proiezione sulla sagoma SVG della Puglia e rango amministrativo — campi
     additivi della riga di elenco (`ComuneSintesiSerializer`, 01/09/2026),
     aggiunti per togliere dal bundle i 257 nodi di `comuni-nodes.json`.

     `x`/`y` sono coordinate nel `viewBox` della sagoma, NON latitudine e
     longitudine (che restano in `coordinate`): un comune può avere le une
     senza le altre. Sono `null` quando la proiezione non è stata calcolata,
     e in quel caso il comune semplicemente NON si disegna — mai a (0,0),
     che sarebbe un punto inventato nell'angolo della mappa.

     Opzionali perché le fixture del dataset storico (`dev-data/comuni.json`,
     schema KB Arianna) non li hanno: `undefined` = la sorgente non dichiara
     il campo, che è diverso da `null` = dichiarato assente. */
  readonly x?: number | null;
  readonly y?: number | null;
  /** `true` per i capoluoghi di provincia (8 in Puglia: BAT ne ha tre). */
  readonly capoluogo?: boolean;
}

/**
 * `ComuneNodo` — la riga di `GET /api/v2/comuni/nodi/`.
 *
 * È volutamente PIÙ POVERA di `ComuneSintesi`: esattamente i campi con cui si
 * costruiscono il menù «Comuni», il picker dell'hero e la mappa decorativa,
 * e nient'altro. Su 257 righe servite in blocco ogni campo in più è peso che
 * nessuno legge — `codice_istat`, `cap`, `popolazione`, `piano` non servono a
 * nessuna delle tre interfacce, e chi li vuole ha `/comuni/` e
 * `/comuni/{slug}/`.
 *
 * Il nome esteso della provincia NON c'è: la sigla basta, e i nomi delle sei
 * province sono una costante amministrativa (`features/mappa/comuni.ts`).
 */
export interface ComuneNodo {
  readonly slug: string;
  readonly nome: string;
  /** SIGLA (`BR`). */
  readonly provincia: SiglaProvincia;
  readonly capoluogo: boolean;
  /**
   * Posizione sul `viewBox` della sagoma della Puglia — NON una coordinata
   * geografica (quelle sono in `coordinate`). `null` = il comune non ha una
   * posizione sulla sagoma, e allora **non si disegna**: mai un ripiego su
   * (0,0). Il sistema di riferimento è dichiarato da `meta.viewbox`.
   */
  readonly x: number | null;
  readonly y: number | null;
  readonly coordinate: Coordinate | null;
}

/** `ComuneDettaglio` — il payload di `/api/v2/comuni/{slug}/`. */
/** `AudioGuida` — traccia audio della scheda comune (additivo 31/07/2026).
 *  `titolo` può essere vuoto e `durata_secondi` `null`: il riferimento non
 *  li mostra. */
export interface AudioGuidaComune {
  readonly url: string;
  readonly titolo: string;
  readonly durata_secondi: number | null;
  readonly lingua: string;
}

/** `SezioneNarrativa` — sezione numerata della panoramica (additivo
 *  31/07/2026). `ordine` è dato REDAZIONALE: si rende a due cifre («01») e
 *  non si riordina lato UI. */
export interface SezioneNarrativa {
  readonly ordine: number;
  readonly titolo: string;
  readonly testo: string;
  /**
   * Gemello in SOLA LETTURA di `testo`, senza marcatura (decisione 96 del
   * backend). Da usare ovunque l'HTML sia un difetto e non un contenuto:
   * sintesi vocale, `<meta>`, anteprime, ricerca. Non esiste in scrittura.
   */
  readonly testo_semplice?: string;
  readonly lingua: string;
  readonly fonte: string | null;
}

export interface ComuneDettaglio extends ComuneSintesi {
  readonly altitudine_m?: number | null;
  /** Decimale come stringa. */
  readonly superficie_kmq?: DecimalString | null;
  readonly sindaco?: string;
  readonly data_aggiornamento?: string | null;
  readonly versione_schema?: string;

  readonly contatti: Contatti;
  readonly identita_narrativa: IdentitaNarrativa | null;
  readonly mappa: Mappa | null;
  readonly overview: Overview | null;
  readonly wikipedia: EstrattoWikipedia | null;

  /* Campi additivi del 31/07/2026 (contratto-api-v2.md, allineamento al
     riferimento). Opzionali QUI perché le fixture del dataset storico non
     li hanno: `undefined` (fixtures) e `null`/`[]` (API senza contenuto)
     si rendono allo stesso modo — come assenza. */
  /** Traccia audio registrata del comune; `null` finché nessuno la fornisce.
      Il riferimento non ne pubblica: la striscia «AUDIO GUIDA» usa la
      sintesi vocale del browser finché questo campo è nullo. */
  readonly audio_guida?: AudioGuidaComune | null;
  /** Sezioni numerate della panoramica («01 CENNO DI STORIA»…): array
      ORDINATO dalla redazione — la UI non riordina. Vuoto = non curate. */
  readonly sezioni_narrative?: readonly SezioneNarrativa[];

  readonly servizi: readonly ServizioComunale[];
  readonly scuole: readonly Scuola[];
  readonly biblioteche: readonly Biblioteca[];
  readonly sanita: readonly StrutturaSanitaria[];
  readonly sociale: readonly ServizioSociale[];
  readonly trasporti: readonly Trasporto[];
  readonly strutture_ricettive: readonly StrutturaRicettiva[];
  readonly punti_interesse: readonly PuntoInteresse[];

  /** Obbligatoria nello schema. **Non va ricalcolata dal frontend.** */
  readonly copertura: Copertura;

  /* NOTA: `eventi_riferiti` e `news_riferite` NON esistono su questo tipo e
     non vanno aggiunti. Gli eventi e le storie di un comune si ottengono con
     `?comune__slug={slug}` sui rispettivi endpoint: è più REST, permette
     paginazione e ordinamento, e non gonfia la scheda comune. */
}

// ---------------------------------------------------------------------------
// Eventi, storie, news, bandi, itinerari
// ---------------------------------------------------------------------------

export type StatoEvento = 'cancelled' | 'draft' | 'published' | 'stub';

export type TipoEvento =
  | 'altro'
  | 'concerto'
  | 'convegno'
  | 'festa_patronale'
  | 'festival'
  | 'fiera'
  | 'mostra'
  | 'religiosa'
  | 'rievocazione'
  | 'sagra'
  | 'sport'
  | 'teatro'
  | 'tradizione';

/**
 * `quando` — collocazione temporale con **`certezza` esplicita**.
 *
 * Dalla descrizione nello schema: *"362 eventi su 362 nel dataset storico non
 * hanno data esatta: la SPA deve poter distinguere '4 settembre 2026' da
 * 'agosto' senza inventare un timestamp."*
 *
 * È il vincolo "niente dati inventati" applicato al tempo: l'interfaccia deve
 * mostrare "agosto" quando è quello che si sa, **non** un 1° agosto inventato.
 * La forma interna non è dichiarata nello schema: il tipo resta permissivo.
 */
export interface QuandoEvento {
  readonly certezza?: string;
  readonly inizio?: string | null;
  readonly fine?: string | null;
  readonly testo?: string | null;
  readonly [key: string]: unknown;
}

/**
 * `EventoCalendario` — la voce di `GET /eventi/calendario/` (decisione 205):
 * TUTTI gli eventi pubblicati, senza paginazione, in forma leggera. Esiste
 * perché la lista paginata tronca (622 eventi, tetto server a 200) e un
 * calendario alimentato da una pagina mostra mesi vuoti che vuoti non sono.
 * `Evento` la estende: ovunque serva la forma leggera, quella piena va bene.
 */
export interface EventoCalendario {
  readonly slug: string;
  readonly titolo: string;
  readonly tipo?: TipoEvento;
  /** Slug del comune, con underscore (vincolo 3). */
  readonly comune: string;
  readonly comune_nome: string;
  readonly provincia: SiglaProvincia;
  readonly quando: QuandoEvento;
}

export interface Evento extends EventoCalendario {
  readonly descrizione?: string;
  /**
   * Gemello in SOLA LETTURA di `descrizione`, senza marcatura (decisione 96 del
   * backend). Da usare ovunque l'HTML sia un difetto e non un contenuto:
   * sintesi vocale, `<meta>`, anteprime, ricerca. Non esiste in scrittura.
   */
  readonly descrizione_semplice?: string;
  readonly lingua?: string;
  readonly venue?: string;
  readonly indirizzo?: string;
  readonly coordinate: Coordinate | null;
  readonly url_ufficiale?: string | null;
  readonly cover_url?: string | null;
  readonly stato?: StatoEvento;
  readonly qualita?: string;
  readonly tag?: unknown;
  readonly fonte: string;
  readonly organizzatore: Readonly<Record<string, unknown>> | null;
}

export interface Storia {
  readonly slug: string;
  readonly tipo: string;
  readonly categoria: string;
  readonly titolo: string;
  readonly sottotitolo?: string;
  readonly testo: string;
  /**
   * Gemello in SOLA LETTURA di `testo`, senza marcatura (decisione 96 del
   * backend). Da usare ovunque l'HTML sia un difetto e non un contenuto:
   * sintesi vocale, `<meta>`, anteprime, ricerca. Non esiste in scrittura.
   */
  readonly testo_semplice?: string;
  readonly lingua?: string;
  readonly comune: string;
  readonly provincia: SiglaProvincia;
  readonly anno?: number | null;
  readonly thumbnail_url?: string | null;
  readonly tag?: unknown;
  readonly fonte: string;
  readonly fonte_url?: string | null;
  /** Più licenze per una sola storia: il caso composito è previsto. */
  readonly licenze: readonly string[];
  readonly licenza_composita?: boolean;
  readonly approvata_per_pubblicazione?: boolean;
  readonly data_aggiornamento?: string | null;
}

/** `CategoriaBando` — dallo schema (`components.schemas.CategoriaBando`). */
export interface CategoriaBando {
  readonly codice: string;
  readonly etichetta?: string;
  /** Esadecimale della sorgente (es. `#10B981`): il filetto delle card. */
  readonly colore?: string;
  readonly icona?: string;
}

/** `StatoBando` — enum chiuso nello schema. */
export type StatoBando =
  | 'aperto'
  | 'in_scadenza'
  | 'chiuso'
  | 'sportello_continuo'
  | 'non_noto';

/**
 * `Bando` — dallo schema OpenAPI (`components.schemas.Bando`).
 * `/api/v2/news/` e `/api/v2/bandi/` servono la STESSA risorsa (la pagina
 * News del riferimento elenca bandi e opportunità, non notizie).
 *
 * `scadenza_effettiva` è dichiarata `object` nello schema: data quando
 * esiste, testo quando la sorgente dice «Sportello continuo». Si legge con
 * type-guard, non si forza.
 */
export interface Bando {
  readonly slug: string;
  readonly titolo: string;
  readonly sommario?: string;
  readonly descrizione?: string;
  /**
   * Gemello in SOLA LETTURA di `descrizione`, senza marcatura (decisione 96 del
   * backend). Da usare ovunque l'HTML sia un difetto e non un contenuto:
   * sintesi vocale, `<meta>`, anteprime, ricerca. Non esiste in scrittura.
   */
  readonly descrizione_semplice?: string;
  /** NULLABILE: sui dati reali 9 bandi su 24 arrivano con `categoria: null`
   *  (verifica strumentale 31/07/2026) — la tassonomia valorizzata è spesso
   *  solo `categoria_dichiarata`. L'assenza si mostra come assenza (niente
   *  chip), mai dereferenziata alla cieca. */
  readonly categoria: CategoriaBando | null;
  /** Etichetta libera della sorgente, es. «Avviso pubblico». */
  readonly categoria_dichiarata?: string;
  readonly ente?: string;
  /** Testo, non numero: la sorgente scrive «fino a 150.000 euro». */
  readonly importo_testo?: string;
  readonly destinatari?: string;
  readonly territorio?: string;
  readonly data_pubblicazione?: string | null;
  readonly scadenza_effettiva: Readonly<Record<string, unknown>>;
  readonly stato?: StatoBando;
  readonly url?: string;
  readonly rilevanza?: number | null;
  readonly archiviato?: boolean;
  readonly fonte: string;
}

/** `/api/v2/news/` è un alias di `/bandi/`: stesso schema `Bando`. */
export type News = Bando;

// ---------------------------------------------------------------------------
// Attività «Gusta la Puglia» (contratti nuovi del 31/07/2026)
// ---------------------------------------------------------------------------

/** `GET /attivita/categorie/` — risposta piatta, non paginata. */
export interface CategoriaAttivita {
  readonly codice: string;
  readonly etichetta: string;
}

/**
 * Voce del menù digitale. `allergeni` sono gli **id 1–14 dell'allegato II
 * del Reg. UE 1169/2011**: la legenda la rende la UI (i18n), l'API non la
 * duplica. `prezzo` è testuale come tutti gli importi del contratto.
 */
export interface VoceMenu {
  readonly nome: string;
  readonly descrizione?: string;
  readonly prezzo?: string;
  readonly allergeni: readonly number[];
  readonly vegetariano: boolean;
  readonly vegano: boolean;
  readonly senza_glutine: boolean;
  readonly tipico: boolean;
  readonly prodotti_locali: boolean;
  readonly surgelato: boolean;
  readonly abbattuto: boolean;
}

export interface SezioneMenu {
  readonly nome: string;
  readonly voci: readonly VoceMenu[];
}

export interface MenuAttivita {
  readonly titolo: string;
  readonly aggiornato_il: string | null;
  readonly sezioni: readonly SezioneMenu[];
}

/**
 * `Attivita` (sintesi) — 193 schede reali del pilota Brindisi, base
 * OpenStreetMap **ODbL 1.0** (share-alike): l'attribuzione arriva in
 * `meta.attribuzione` (fonte `gusta_osm`) e VA mostrata.
 * `demo: true` marca la scheda dimostrativa (l'unica col menù).
 */
export interface Attivita {
  readonly slug: string;
  readonly nome: string;
  readonly categoria: string;
  readonly categoria_etichetta: string;
  readonly cucine: readonly string[];
  /** Slug del comune, con underscore (vincolo 3). */
  readonly comune: string;
  readonly comune_nome: string;
  readonly indirizzo: string;
  readonly coordinate: { readonly lat: number; readonly lon: number } | null;
  /** Testuale («€€-€€€»), vuota per 155 schede su 193. */
  readonly fascia_prezzo: string;
  readonly stato: 'importata' | 'approvata';
  readonly demo: boolean;
  readonly fonte: string;
  /** Arricchimento dichiarato per scheda, es. «OSM + TripAdvisor (giu 2026)». */
  readonly fonte_dichiarata: string;
}

/** Dettaglio: i tag (`servizi`, `accessibilita`, …) sono DICHIARATI dalla
 *  sorgente, non verificati — la UI lo dice. */
export interface AttivitaDettaglio extends Attivita {
  readonly categorie_secondarie: readonly { readonly codice: string; readonly etichetta: string }[];
  readonly descrizione_breve: string;
  readonly descrizione: string;
  /**
   * Gemello in SOLA LETTURA di `descrizione`, senza marcatura (decisione 96 del
   * backend). Da usare ovunque l'HTML sia un difetto e non un contenuto:
   * sintesi vocale, `<meta>`, anteprime, ricerca. Non esiste in scrittura.
   */
  readonly descrizione_semplice?: string;
  readonly telefono: string;
  readonly email: string;
  readonly sito: string;
  readonly whatsapp: string;
  readonly orari: string;
  readonly servizi: readonly string[];
  readonly accessibilita: readonly string[];
  readonly alimentazione: readonly string[];
  readonly lingue: readonly string[];
  readonly confidenza: number | null;
  readonly data_rilevazione: string | null;
  readonly menu: MenuAttivita | null;
}

// ---------------------------------------------------------------------------
// Documenti sfogliabili (flipbook — contratto del 31/07/2026)
// ---------------------------------------------------------------------------

export interface PaginaDocumento {
  readonly numero: number;
  /** URL sotto `/media/…` (servite da nginx). */
  readonly immagine: string;
  readonly miniatura: string | null;
  /** Testo della pagina: alimenta la ricerca interna; vuoto se solo grafica. */
  readonly testo: string;
}

/** `GET /documenti/{slug}/` — es. `mappa-multimediale-brindisi` (32 pagine
 *  JPG 1358×1920, licenza PROPRIETARIA del Comune di Brindisi). */
export interface DocumentoSfogliabile {
  readonly slug: string;
  readonly titolo: string;
  readonly descrizione: string;
  readonly larghezza_pagina_px: number;
  readonly altezza_pagina_px: number;
  readonly pagine_totali: number;
  readonly fonte: string;
  readonly pagine: readonly PaginaDocumento[];
}

// ---------------------------------------------------------------------------
// Itinerari, tappe e categorie — contratto §I (decisioni 164–172, 06/08/2026)
// ---------------------------------------------------------------------------
//
// CAMBIO DI CONTRATTO, non additivo. Spariscono `gruppo`, `categoria` come
// stringa d'enum e `durata_giorni`; `tappe[]` non è più un elenco di comuni.
// Una tappa punta a UN luogo censito (punto di interesse, struttura ricettiva
// o attività) ed è riutilizzabile in più itinerari; ciò che dipende dalla
// sequenza — ordine, nota, distanza e tempo dalla precedente — sta sul legame.

/**
 * `CategoriaItinerario` — tassonomia unica, globale, usata SIA dagli itinerari
 * (una) SIA dalle tappe (più di una). Non è più un enum: è un'entità gestita
 * dalla dashboard, quindi il client non può conoscerne i valori in anticipo.
 *
 * `itinerari_totali` arriva solo dagli endpoint `/itinerari/categorie/` e
 * conta i soli PUBBLICATI: una categoria a zero esiste comunque e va resa con
 * l'elenco vuoto, non con un 404 (§I.3).
 */
export interface CategoriaItinerario {
  readonly slug: string;
  readonly nome: string;
  readonly descrizione?: string;
  /** Emoji. Vuota = niente icona, mai un segnaposto. */
  readonly icona?: string;
  /** Esadecimale dichiarato dalla redazione, es. `#0D47A1`. */
  readonly colore?: string;
  readonly ordine?: number;
  readonly copertina_url?: string;
  readonly itinerari_totali?: number;
}

/** Il discriminante del riferimento polimorfico di una tappa. */
export type TipoLuogoTappa = 'punto_interesse' | 'struttura_ricettiva' | 'attivita';

/**
 * `LuogoTappa` — il luogo censito a cui la tappa punta, già risolto dal server.
 *
 * `chiave` NON è uniforme, e non per distrazione: è l'identificatore che
 * l'API pubblica usa per quel tipo — `slug` per `attivita`
 * (`GET /attivita/{slug}/`), chiave primaria per `punto_interesse` e
 * `struttura_ricettiva`. Costruire un link richiede quindi di guardare `tipo`.
 *
 * `fonte` viaggia per riga perché un itinerario può mescolare licenze: beni
 * MiC (CC BY 4.0) e attività su base OpenStreetMap (ODbL, share-alike).
 */
export interface LuogoTappa {
  readonly tipo: TipoLuogoTappa | (string & {});
  readonly chiave: string | number;
  readonly nome: string;
  /** Slug del comune, con underscore (vincolo 3). */
  readonly comune: string | null;
  readonly comune_nome: string | null;
  readonly indirizzo?: string;
  /** `{lat, lon}` di stringhe decimali, oppure `null` se il luogo non le ha. */
  readonly coordinate: Coordinate | null;
  readonly immagine_url?: string;
  readonly fonte?: string | null;
}

/**
 * `TappaPubblica` — una voce di `tappe[]` sul dettaglio.
 *
 * Due regole del contratto che questo tipo esiste per rendere visibili:
 *
 *  · **`titolo` è già risolto** (decisione 166): override della tappa se c'è,
 *    nome del luogo altrimenti. Il client NON scrive `titolo || luogo.nome`;
 *  · **i cumulati sono `null` da dove il dato manca in poi**. `null` significa
 *    «da qui il conto è incompleto», non «zero»: mostrarlo come 0 km sarebbe
 *    un numero inventato (vincolo 4).
 */
export interface TappaPubblica {
  /** Id del LEGAME: è quello che il riordino usa. */
  readonly id: number;
  /** Id della TAPPA, stabile fra gli itinerari in cui compare. */
  readonly tappa_id: number;
  /** Posizione nella sequenza, 1-based. */
  readonly numero: number;
  readonly titolo: string;
  /**
   * SLUG di categoria, non oggetti — è quanto dichiara lo schema
   * (`TappaPubblica.categorie: array of string`), verificato sulla risposta
   * viva. I nomi leggibili stanno in `categorie_tappe[]` del dettaglio: è la
   * faccetta a portare nome, icona e colore, e la scheda li risolve da lì
   * invece di ripetere la stessa tabella su ogni tappa.
   */
  readonly categorie: readonly string[];
  /** Immagine propria con ripiego su quella del luogo; `""` se non c'è. */
  readonly immagine_url?: string;
  readonly sosta_minuti?: number | null;
  /** Nota redazionale su QUESTO passaggio. */
  readonly nota?: string;
  readonly distanza_da_precedente_km?: DecimalString | null;
  readonly tempo_da_precedente_minuti?: number | null;
  readonly distanza_cumulata_km?: DecimalString | null;
  readonly tempo_cumulato_minuti?: number | null;
  /** `null` è ammesso dallo schema: una tappa il cui luogo non è più leggibile. */
  readonly luogo: LuogoTappa | null;
}

/** L'unità di una durata dichiarata. Il contratto ne ammette due sole. */
export type UnitaDurata = 'ore' | 'giorni';

/** La resa leggibile di una quantità di minuti, come la calcola il server. */
export interface DurataCalcolata {
  readonly minuti: number | null;
  readonly valore: number | null;
  readonly unita: UnitaDurata | '' | (string & {});
  /** `false` = qualche tappa non dichiara sosta o tratta. */
  readonly completa: boolean;
}

/**
 * `Durata` — **entrambi i numeri, sempre** (§I.4).
 *
 * La sovrascrittura redazionale vince (`valore`/`unita`) ma il calcolo resta
 * visibile: uno scarto di dieci ore è un segnale, e con la sola durata
 * vincente sarebbe invisibile. La conversione minuti → giorni usa una
 * giornata di 8 ore (480 minuti): è una convenzione dichiarata, e i minuti
 * viaggiano accanto perché chi non la condivide possa rifare il conto.
 *
 * Con `origine: "calcolata"` e nessuna tappa, `valore` è `null` e `unita` è
 * `""`: nessuna durata, non «zero ore».
 */
export interface Durata {
  readonly valore: number | null;
  readonly unita: UnitaDurata | '' | (string & {});
  readonly origine: 'calcolata' | 'redazionale' | (string & {});
  readonly calcolata: DurataCalcolata;
  readonly dichiarata: {
    readonly valore: number;
    readonly unita: UnitaDurata | (string & {});
    readonly minuti: number;
  } | null;
  /** `dichiarata.minuti − calcolata.minuti`, o `null` se non c'è confronto. */
  readonly scarto_minuti: number | null;
}

/**
 * `ProfiloPercorrenza` — i cumulati, calcolati dal server e mai ricalcolati qui.
 *
 * I flag `*_completa` e i contatori `tratte_senza_*` esistono perché la UI
 * possa scrivere «15 km su 4 tratte, 1 non misurata» invece di un totale che
 * sembra esatto. `lunghezza_dichiarata_km` NON viene riconciliata con
 * `distanza_totale_km`: sono due dati diversi e il server non ne sceglie uno.
 */
export interface ProfiloPercorrenza {
  readonly tappe: number;
  readonly distanza_totale_km: DecimalString | null;
  readonly distanza_completa: boolean;
  readonly tratte_senza_distanza: number;
  readonly tempo_trasferimenti_minuti: number | null;
  readonly tempo_soste_minuti: number | null;
  readonly tempo_totale_minuti: number | null;
  readonly tempo_completo: boolean;
  readonly tratte_senza_tempo: number;
  readonly tappe_senza_sosta: number;
  readonly lunghezza_dichiarata_km: number | null;
}

/**
 * Una voce di `categorie_tappe[]`: la faccetta del filtro INTERNO all'itinerario.
 *
 * Il filtro è del client apposta. Filtrare le tappe sul server cambierebbe la
 * sequenza e quindi i cumulati, producendo un profilo di percorrenza che non
 * corrisponde a nessun viaggio reale.
 */
export interface FaccettaCategoriaTappa {
  readonly slug: string;
  readonly nome: string;
  readonly colore?: string;
  readonly icona?: string;
  /** Quante tappe di questo itinerario hanno la categoria. */
  readonly tappe: number;
}

/**
 * `Itinerario` — riga di `GET /itinerari/` (§I.1).
 *
 * `comuni` è la PROIEZIONE della sequenza: i comuni attraversati in ordine di
 * percorrenza, senza ripetizioni consecutive. Non è più una relazione del
 * modello, quindi non si conta più «comuni distinti» dalle tappe.
 * `tappe_totali` conta solo le tappe pubblicate.
 */
export interface Itinerario {
  readonly slug: string;
  readonly titolo: string;
  readonly sottotitolo?: string;
  readonly testo?: string;
  /**
   * Gemello in SOLA LETTURA di `testo`, senza marcatura (decisione 96 del
   * backend). Da usare ovunque l'HTML sia un difetto e non un contenuto:
   * sintesi vocale, `<meta>`, anteprime, ricerca. Non esiste in scrittura.
   */
  readonly testo_semplice?: string;
  /** Oggetto oppure `null`: un itinerario senza categoria è ammesso. */
  readonly categoria: CategoriaItinerario | null;
  readonly tono: 'istituzionale' | 'divulgativo';
  /** Emoji usata dalla UI. */
  readonly icona?: string;
  readonly tag?: unknown;
  /** Lunghezza DICHIARATA. Indipendente dalla somma delle tratte. */
  readonly lunghezza_km?: number | null;
  readonly superficie_kmq?: number | null;
  readonly stagione_consigliata?: string;
  /**
   * I quattro campi di scheda della decisione 228 (backend). Valgono per
   * QUALUNQUE itinerario — compilati a mano o dal configuratore — e
   * l'assenza è assenza: `''`/`null` = non dichiarato, mai un default.
   */
  readonly mezzo_consigliato?: 'piedi' | 'bici' | 'auto' | '' | (string & {});
  /** Somma degli ingressi noti; `null` se il totale non è completo. */
  readonly costo_indicativo?: DecimalString | null;
  /** MINIMO sui criteri richiesti lungo le tappe; `''` = non valutata. */
  readonly livello_accessibilita?: 'piena' | 'parziale' | 'assente' | '' | (string & {});
  /** Tappe su prenotazione, coi riferimenti dichiarati. `''` = nessuna nota. */
  readonly avviso_prenotazioni?: string;
  readonly durata: Durata;
  readonly tappe_totali: number;
  readonly comuni: readonly string[];
  readonly fonte: string | null;
  readonly fonte_url?: string;
  /** Attribuzione TESTUALE dichiarata dalla sorgente (additivo 31/07/2026):
      il «Fonte: …» in corsivo a fondo scheda del riferimento. Vuota = non
      dichiarata. Non sostituisce `fonte`/`fonte_url` (registro fonti,
      vincolo 5): è il testo di cortesia per la card. */
  readonly fonte_dichiarata?: string;
}

/** `GET /itinerari/{slug}/` — tutto l'elenco, più la sequenza e i cumulati. */
export interface ItinerarioDettaglio extends Itinerario {
  readonly tappe: readonly TappaPubblica[];
  readonly profilo: ProfiloPercorrenza;
  readonly categorie_tappe: readonly FaccettaCategoriaTappa[];
}

// ---------------------------------------------------------------------------
// Pagine redazionali — decisioni 28–42
// ---------------------------------------------------------------------------

/** `AreaPagina` — enum chiuso del backend. */
export type AreaPagina = 'progetto' | 'territorio' | 'servizi' | 'rete_civica' | 'legale';

/**
 * I blocchi tipizzati (decisioni 29 e 40). La fonte di verità della forma è
 * `backend/apps/contenuti/blocchi.py`: questo union DEVE restarle allineato.
 * Il renderer gestisce anche il caso fuori-union (`BloccoSconosciuto`):
 * un tipo nuovo lato backend non deve rompere la pagina, deve dichiararsi.
 */
export type Blocco =
  | { readonly tipo: 'titolo'; readonly testo: string; readonly livello: 2 | 3 | 4 }
  | { readonly tipo: 'prosa'; readonly testo: string }
  | { readonly tipo: 'elenco'; readonly voci: readonly string[]; readonly ordinato?: boolean }
  /* `url` ammette anche `mailto:` e `tel:` — SOLO sul blocco cta (coda
     rimisura 31/07/2026): per immagini, video e card restano vietati. */
  | { readonly tipo: 'cta'; readonly etichetta: string; readonly url: string }
  | { readonly tipo: 'nota'; readonly testo: string; readonly variante: 'info' | 'avviso' }
  | {
      readonly tipo: 'immagine';
      readonly url: string;
      /** Obbligatori nel contratto (decisione 37): mai assenti. */
      readonly alt: string;
      readonly fonte: string;
      readonly licenza: string;
      readonly didascalia?: string;
    }
  | {
      readonly tipo: 'tabella';
      readonly didascalia: string;
      /** Coda rimisura 31/07/2026 (voce 20): `true` = la didascalia resta il
       *  NOME ACCESSIBILE (`<caption>` sr-only) ma non si vede — l'importatore
       *  la ricava dall'h2 di sezione e mostrarla la farebbe leggere due
       *  volte. MAI omettere la caption: solo nasconderla visivamente. */
      readonly didascalia_nascosta?: boolean;
      readonly intestazioni: readonly string[];
      readonly righe: readonly (readonly string[])[];
    }
  | {
      readonly tipo: 'faq';
      readonly voci: readonly { readonly domanda: string; readonly risposta: string }[];
    }
  /* `accordion` (voce 32 dei residui, chiusura 31/07/2026): i `<details>`
     del riferimento con corpo STRUTTURATO (stesso registro dei blocchi).
     Regole del contratto: elementi ≥1, titolo non vuoto, blocchi ≥1 e MAI
     un accordion annidato (doppio espandi/comprimi = labirinto per
     tastiera e screen reader). La `faq` resta per i casi a testo piano. */
  | {
      readonly tipo: 'accordion';
      readonly elementi: readonly {
        readonly titolo: string;
        /** Voce 41: badge nel `<summary>` ACCANTO al titolo (mai al suo
         *  posto), ≤ 40 caratteri («Il problema», «La soluzione»…). */
        readonly distintivo?: string;
        readonly blocchi: readonly Blocco[];
      }[];
    }
  /* Voce 41: la fascia numerica IN RIGA del riferimento («257 Comuni ·
     6 Province · …») — prima cadeva in `griglia_card` e diventava card
     separate. `valore` obbligatorio, `etichetta` facoltativa. */
  | {
      readonly tipo: 'statistiche';
      readonly voci: readonly { readonly valore: string; readonly etichetta?: string }[];
    }
  /* Voce 41: etichette brevi affiancate (≤ 80 caratteri l'una — il limite è
     semantico: una frase non è una chip, sopra gli 80 l'importatore ripiega
     su `elenco`). */
  | { readonly tipo: 'chip'; readonly voci: readonly string[] }
  | { readonly tipo: 'citazione'; readonly testo: string; readonly autore?: string }
  | { readonly tipo: 'video'; readonly url: string; readonly titolo: string }
  | {
      readonly tipo: 'griglia_card';
      readonly card: readonly {
        readonly titolo: string;
        readonly testo?: string;
        readonly url?: string;
      }[];
    }
  /* -- tipi aggiunti il 01/08/2026 (contratto §22, requisito 16) ------------
     `galleria`: più immagini con la STESSA disciplina di attribuzione del
     blocco `immagine` — alt, fonte e licenza per OGNI foto (decisione 37,
     vincolo 5). Dodici foto senza attribuzione sono dodici violazioni di
     licenza, non una scorciatoia. */
  | {
      readonly tipo: 'galleria';
      readonly immagini: readonly {
        readonly url: string;
        readonly alt: string;
        readonly fonte: string;
        readonly licenza: string;
        readonly didascalia?: string;
      }[];
      readonly didascalia?: string;
    }
  /* `mappa`: mappa Leaflet nel corpo della pagina. `titolo` è obbligatorio
     come per `video` e `tabella` — è il nome accessibile del riquadro, e
     senza uno screen reader annuncia «mappa» e basta. */
  | {
      readonly tipo: 'mappa';
      readonly titolo: string;
      readonly centro_lat: number;
      readonly centro_lon: number;
      readonly zoom?: number;
      readonly punti?: readonly {
        readonly lat: number;
        readonly lon: number;
        readonly etichetta: string;
        readonly url?: string;
      }[];
      readonly didascalia?: string;
    }
  /* `embed`: contenuto di un servizio esterno. Solo `https://`, `titolo`
     (nome accessibile) e `fonte` obbligatori — incorporare contenuto di terzi
     senza dire di chi è non è ammissibile su un portale della PA.
     ⚠️ La CSP del portale è `default-src 'self'` senza `frame-src`: l'iframe
     NON caricherebbe. Il renderer lo dichiara e offre il link. */
  | {
      readonly tipo: 'embed';
      readonly url: string;
      readonly titolo: string;
      readonly fonte: string;
      readonly altezza?: number;
      readonly didascalia?: string;
    };

/** Il payload di `GET /api/v2/pagine/{slug}/`. */
export interface PaginaRedazionale {
  readonly slug: string;
  readonly area: AreaPagina;
  /** Lingua della traduzione SERVITA — può essere il fallback italiano. */
  readonly lingua: string;
  /** `false` = si sta leggendo il fallback: va detto in pagina (decisione 36). */
  readonly tradotta: boolean;
  readonly titolo: string;
  readonly sottotitolo: string;
  /** Occhiello di testata (voce 48, metadato versionato ≤120): VUOTO =
   *  niente occhiello in pagina — MAI un ripiego generato (l'occhiello
   *  «SERVIZI» duplicato della voce 27 era esattamente quell'errore). */
  readonly occhiello: string;
  readonly blocchi: readonly Blocco[];
  /** Per le legali (decisione 34): la data da cui il testo vale. */
  readonly decorrenza: string | null;
  readonly versione: number;
  readonly pubblicata_il: string | null;
}

// ---------------------------------------------------------------------------
// Autenticazione — cookie httpOnly (decisione 43)
// ---------------------------------------------------------------------------

/**
 * I token JWT non passano dal JavaScript: stanno in cookie httpOnly
 * (`jwtCookieAuth` nello schema). L'header `Authorization: Bearer` resta nel
 * contratto per i client di servizio, ma la SPA non lo usa più: vedi
 * `api/auth.ts`.
 */
export interface UtenteCorrente {
  readonly email: string;
  readonly nome_visualizzato: string;
  readonly ruolo: string;
  readonly stato: string;
  /** Slug dei comuni su cui l'utente può operare (vincolo 8). */
  readonly comuni: readonly string[];
  /** `true` = nessun vincolo di comune (redazione centrale). */
  readonly ambito_illimitato: boolean;
}

// ---------------------------------------------------------------------------
// Errori
// ---------------------------------------------------------------------------

/**
 * Forma degli errori — **verificata**, non più ipotizzata.
 *
 * ```json
 * { "errore": { "codice": "...", "messaggio": "...", "dettagli": { ... } } }
 * ```
 *
 * Non è il default di DRF (`{detail}`), ed è meglio così: **`codice` è
 * ramificabile**. Per distinguere `transizione_non_valida` da
 * `email_non_configurata` con `{detail}` servirebbe il pattern matching su una
 * frase in italiano, che si rompe alla prima riformulazione del messaggio.
 * `dettagli` **è** il `{campo: [messaggi]}` della validazione, quindi non si
 * perde nulla rispetto alla forma DRF.
 */
export interface DettaglioErrore {
  readonly codice: string;
  readonly messaggio: string;
  /** Per `validazione` è `{campo: [messaggi]}`. Altrimenti dipende dal codice. */
  readonly dettagli?: Readonly<Record<string, unknown>>;
}

export interface RispostaErrore {
  readonly errore: DettaglioErrore;
}

/**
 * I codici applicativi documentati nello schema (con esempio).
 * L'unione resta aperta con `(string & {})`: il backend può aggiungerne, e un
 * codice sconosciuto non deve essere un errore di compilazione.
 */
export type CodiceErrore =
  | 'validazione' // 400 — `dettagli` = {campo: [messaggi]}
  | 'not_authenticated' // 401
  | 'permission_denied' // 403
  | 'not_found' // 404
  | 'transizione_non_valida' // 409 — `dettagli` = {da, a}
  | 'throttled' // 429
  | 'email_non_configurata' // 503
  | (string & {});

/** Errori di validazione per campo, come li restituisce `dettagli` su 400. */
export type ErroriPerCampo = Readonly<Record<string, readonly string[]>>;

/**
 * Estrae `{campo: [messaggi]}` da `dettagli`, scartando ciò che non ha quella
 * forma. Serve a collegare gli errori ai campi del form (WCAG 3.3.1) senza
 * dare per scontato che `dettagli` sia sempre quello.
 */
export function erroriPerCampo(
  dettagli: Readonly<Record<string, unknown>> | undefined,
): ErroriPerCampo {
  if (!dettagli) return {};
  const risultato: Record<string, readonly string[]> = {};
  for (const [campo, valore] of Object.entries(dettagli)) {
    if (Array.isArray(valore) && valore.every((v) => typeof v === 'string')) {
      risultato[campo] = valore as readonly string[];
    } else if (typeof valore === 'string') {
      risultato[campo] = [valore];
    }
  }
  return risultato;
}
